import { Link, useNavigate } from "@tanstack/react-router";
import {
  APIError,
  Modes,
  cn,
  createState,
  isPlatformError,
  useAccountMode,
  useAuthStore,
  useResumeTests,
} from "@theprelude/core";
import {
  AlertIcon,
  Button,
  ErrorPage,
  LoadingPage,
  PlayIcon,
} from "@theprelude/ds";
import { Suspense, useEffect } from "react";
import AppHeader from "../header/header";
import { UpdateUserOverlay } from "../update-user/update-user-overlay";

export const NotFoundPage: React.FC<{ data: unknown }> = () => {
  return (
    <div
      className={"flex h-screen w-full flex-col items-center justify-center"}
    >
      <ErrorPage
        errorMessage={"404 Not found"}
        actions={
          <Link to={"/home"}>
            <Button intent="secondary">Go Home</Button>
          </Link>
        }
      />
    </div>
  );
};

export function AppErrorPage({
  error,
  fullScreen,
}: {
  error: unknown;
  fullScreen?: boolean;
}) {
  const navigate = useNavigate();
  const changeAccount = useAuthStore((state) => state.changeAccount);

  if (isPlatformError(error as Error)) {
    throw error;
  }

  let status: number | undefined;
  let message = (error as Error).message;
  if (error instanceof APIError) {
    status = error.status;
    message =
      status === 403 || status === 401
        ? `We could not validate your credentials. Please try again. (Code ${status})`
        : error.message;
  }

  return (
    <div
      className={cn(
        "flex h-full w-full flex-col items-center justify-center",
        fullScreen && "h-screen",
      )}
    >
      <ErrorPage
        type={status === 401 ? "error-account" : "error"}
        errorMessage={message}
        actions={
          <>
            <Button
              intent="secondary"
              onClick={() => {
                window.location.reload();
              }}
            >
              Try again
            </Button>

            <Link to={"/sign-out"}>
              <Button intent="secondary">Sign out</Button>
            </Link>

            <Button
              intent="secondary"
              onClick={() => {
                changeAccount(undefined);
                void navigate({ to: "/account/select" });
              }}
            >
              Switch Account
            </Button>
          </>
        }
      />
    </div>
  );
}

export function Banner({
  message,
  children,
}: {
  message: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex min-h-14 w-full items-center justify-center gap-2 bg-status-yellow p-2 text-halloween-black">
      <AlertIcon className="flex-shrink-0" />
      <div className="flex-grow">{message}</div>
      {children}
    </div>
  );
}

export const useTitleStore = createState<string>("");
export const setPageTitle = (title: string) =>
  useTitleStore.getState().setValue(title);

export function AppShell({
  header,
  children,
}: {
  header?: React.ReactNode;
  children: React.ReactNode;
}) {
  const title = useTitleStore((state) => state.value);
  useEffect(() => {
    document.title = `Prelude | ${title}`;
  }, [title]);

  return (
    <div className="duration-750 flex h-screen w-screen flex-col transition-colors ease-in-out">
      <AllTestPausedBanner />

      <div className="flex flex-1 flex-col overflow-hidden">
        <AppHeader title={title}>{header}</AppHeader>
        <div className="flex flex-1 justify-center overflow-hidden">
          <main className="flex flex-1 flex-col overflow-y-auto">
            <Suspense fallback={<LoadingPage />}>{children}</Suspense>
          </main>
        </div>

        <UpdateUserOverlay />
      </div>
    </div>
  );
}

function AllTestPausedBanner() {
  const mode = useAccountMode();
  const allTestsPaused = mode.data === Modes.FROZEN;
  const resumeTests = useResumeTests();

  if (!allTestsPaused) {
    return null;
  }

  return (
    <Banner message="You have paused all tests. Probes will remain dormant until testing is resumed.">
      <Button
        loading={resumeTests.isPending}
        disabled={resumeTests.isPending}
        icon={<PlayIcon />}
        className="flex-shrink hover:shadow-none"
        intent="primary"
        onClick={() => resumeTests.mutate()}
      >
        Resume all tests
      </Button>
    </Banner>
  );
}
